import { KeyboardEvent, useEffect, useCallback, useRef } from 'react';
import { useDENTContext } from '../../../Services/DENTContext';
import useTooth from '../useTooth';
import { PeriodontalObject } from '../../../Interfaces/PeriodontalObject.interface';

let lastPeriodontalToothNumber: number | undefined;

export function setLastPeriodontalToothForOpening(toothNumber: number) {
	lastPeriodontalToothNumber = toothNumber;
}

interface Props {
	periodontalObject: PeriodontalObject[];
}

export default function usePeriodontalKeyboard({ periodontalObject }: Props) {
	const {
		activeTooth,
		activePeriodontalItem,
		setActivePeriodontalItem,
		handleUpdateTooth,
		selectNextTooth,
	} = useDENTContext();
	const { periodontal } = useTooth();

	const {
		periodontal: { getPeriodontal },
	} = useTooth();

	const getNextTooth = useCallback(() => {
		selectNextTooth();
	}, [selectNextTooth]);

	const onChangeValue = useCallback(
		(value: number) => {
			periodontal[activePeriodontalItem].setDepth(value);

			const currentIndex = periodontalObject.findIndex(
				item => item.type === activePeriodontalItem,
			);

			switch (currentIndex) {
				case -1:
				case periodontalObject.length - 1:
					return;
				default:
					return setActivePeriodontalItem(
						periodontalObject[currentIndex + 1].type,
					);
			}
		},
		[
			activePeriodontalItem,
			periodontal,
			periodontalObject,
			setActivePeriodontalItem,
		],
	);

	const onSubmit = useCallback(async () => {
		if (!activeTooth?.periodontal) return;

		await handleUpdateTooth({ periodontal: getPeriodontal });
		getNextTooth();
	}, [
		activeTooth?.periodontal,
		handleUpdateTooth,
		getPeriodontal,
		getNextTooth,
	]);

	const handleKeyDownRef = useRef<(event: KeyboardEvent) => void>();

	handleKeyDownRef.current = (event: KeyboardEvent) => {
		const { key } = event;

		if (activeTooth && key >= '0' && key <= '9') {
			return onChangeValue(parseInt(key, 10));
		}

		if (activeTooth && key === 'Enter') {
			return onSubmit();
		}

		return;
	};

	useEffect(() => {
		const handler = (event: KeyboardEvent) => {
			handleKeyDownRef.current?.(event);
		};

		// @ts-ignore
		window.addEventListener('keydown', handler);

		return () => {
			// @ts-ignore
			window.removeEventListener('keydown', handler);
		};
	}, []);

	useEffect(() => {
		const current = activeTooth?.number;
		const toothChanged =
			current != null && current !== lastPeriodontalToothNumber;
		const itemInvalid = !periodontalObject.some(
			item => item.type === activePeriodontalItem,
		);

		if (toothChanged || itemInvalid) {
			if (toothChanged) {
				lastPeriodontalToothNumber = current;
			}
			if (periodontalObject[0]) {
				setActivePeriodontalItem(periodontalObject[0].type);
			}
		}
	}, [
		activeTooth?.number,
		periodontalObject,
		activePeriodontalItem,
		setActivePeriodontalItem,
	]);
}
